import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Outlet } from "react-router-dom";
import { Building2, LogOut, Languages, WifiOff, ShieldOff } from "lucide-react";
import useOnlineStatus from "@/hooks/useOnlineStatus";
import ForcePasswordChange from "@/components/common/ForcePasswordChange";

export default function DashboardLayout({ title, nav, testIdPrefix = "nav", children }) {
  const { user, logout, logoutAll } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out of all devices? Every other browser and phone session will be ended.")) return;
    await logoutAll();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      <ForcePasswordChange />
      {/* Sidebar */}
      <aside className="lg:w-64 lg:flex-shrink-0 bg-slate-950 text-slate-300 lg:min-h-screen border-r border-slate-800 lg:sticky lg:top-0 lg:h-screen flex flex-col">
        <div className="p-4 lg:p-6 border-b border-slate-800 flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-white text-slate-950 flex items-center justify-center">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-white">Rana Associates</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
          </div>
        </div>

        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto flex-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-blue-600/15 text-blue-400 border-l-2 border-blue-500"
                    : "text-slate-400 hover:bg-slate-900 hover:text-white"
                }`
              }
              data-testid={`${testIdPrefix}-${item.key}`}
            >
              {item.icon && <item.icon className="h-4 w-4" />}
              {t(item.key) || item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block p-3 border-t border-slate-800">
          <div className="text-xs text-slate-500 px-3 py-2">
            {t("logged_in_as")}: <span className="text-slate-200 truncate block">{user?.name}</span>
          </div>
          {!online && (
            <div
              className="flex items-center gap-2 px-3 py-2 mb-2 rounded-md bg-amber-500/10 text-amber-300 text-xs"
              data-testid="sidebar-offline-badge"
            >
              <WifiOff className="h-3.5 w-3.5" /> Offline — drafts saved locally
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggle}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-900 gap-2"
            data-testid="dashboard-lang-toggle"
          >
            <Languages className="h-4 w-4" /> {lang === "en" ? "हिन्दी" : "English"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-900 gap-2"
            data-testid="dashboard-logout"
          >
            <LogOut className="h-4 w-4" /> {t("logout")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={signOutEverywhere}
            className="w-full justify-start text-slate-500 hover:text-white hover:bg-slate-900 gap-2 text-xs"
            data-testid="dashboard-logout-all"
          >
            <ShieldOff className="h-4 w-4" /> Sign out of all devices
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="lg:hidden bg-slate-950 text-slate-300 px-4 py-2 flex items-center justify-between border-b border-slate-800">
          <span className="text-sm truncate">{user?.name}</span>
          <div className="flex items-center gap-1">
            {!online && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300"
                data-testid="global-offline-badge"
                title="You are offline. Data will sync when back online."
              >
                <WifiOff className="h-3 w-3" /> Offline
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={toggle} className="text-slate-300 hover:text-white hover:bg-slate-900" data-testid="mobile-lang-toggle">
              <Languages className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-slate-300 hover:text-white hover:bg-slate-900"
              data-testid="mobile-logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}
